import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useConstant} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {
  InlineBanner,
  InlineBannerActionProps,
  InlineBannerLayoutTypesEnum,
  InlineBannerTypesEnum,
} from '../../../../../../libs/shared/ui/src/components/banners/inlineBanner/inlineBanner';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';

interface SynopsisHeaderProps {
  onRequestClose?: () => void;
  isPinnedCommentsOnly?: boolean;
}

const StyledHeader = styled.header`
  display: flex;
  flex-direction: column;
  padding: 8px 16px;
  gap: 10px;
  color: ${(p) => p.theme.text};
`;

const StyledTopBarDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  grip-gap: 10px;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  margin-right: -8px;
`;

export const SynopsisHeader: FC<SynopsisHeaderProps> = ({onRequestClose, isPinnedCommentsOnly}) => {
  const theme = useUserTheme();
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const actions: ReadonlyArray<InlineBannerActionProps> = useConstant(() => [
    {
      id: 'help',
      title: <Trans id="synopsis-learn-more">Learn more</Trans>,
      iconName: 'externalLinkSmall',
      onClick: () => openInBrowser('https://help.front.com/en/articles/1164608'),
    },
  ]);

  const title = isPinnedCommentsOnly ? (
    <Trans id="synopsis-pinned-comments">Pinned comments</Trans>
  ) : (
    <Trans id="synopsis-title">Summary</Trans>
  );

  return (
    <StyledHeader>
      <StyledTopBarDiv>
        <div>{title}</div>
        {!isPinnedCommentsOnly && (
          <IconButton
            iconName="info"
            onClick={() => setIsInfoOpen(!isInfoOpen)}
            label={<Trans id="synopsis-toggle-info-box">Toggle info box</Trans>}
            hasHover={isInfoOpen}
            color={theme.greys.shade70}
          />
        )}
        <IconButton
          iconName="close"
          onClick={onRequestClose}
          label={<Trans id="synopsis-close">Close [Esc]</Trans>}
          color={theme.greys.shade70}
        />
      </StyledTopBarDiv>
      {isInfoOpen && (
        <InlineBanner
          type={InlineBannerTypesEnum.INFO}
          layoutType={InlineBannerLayoutTypesEnum.MULTILINE}
          actions={actions}
          title={<Trans id="synopsis-info-title">How do summaries work?</Trans>}
        >
          <Trans id="synopsis-info-content">
            AI summarizes each message and comment to make it easier for you to handle the ongoing request. It
            hides messages and comments that are not helpful, including those that served to handle any
            handled request.
          </Trans>
        </InlineBanner>
      )}
    </StyledHeader>
  );
};
